import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex w-full" }
const _hoisted_2 = { class: "mr-3" }
const _hoisted_3 = { class: "w-full" }
const _hoisted_4 = { class: "flex justify-content-end" }
const _hoisted_5 = { class: "flex flex-column py-1" }
const _hoisted_6 = { class: "pb-1" }
const _hoisted_7 = {
  key: 0,
  class: "mr-1 inline-block"
}
const _hoisted_8 = {
  key: 1,
  class: "mr-1 inline-block"
}
const _hoisted_9 = {
  key: 3,
  class: "mx-2"
}
const _hoisted_10 = {
  key: 4,
  class: "mx-2"
}
const _hoisted_11 = {
  key: 5,
  class: "mx-2"
}
const _hoisted_12 = ["onClick", "textContent"]
const _hoisted_13 = {
  key: 10,
  class: "mx-2"
}
const _hoisted_14 = {
  key: 11,
  class: "mx-2"
}
const _hoisted_15 = {
  key: 0,
  class: "flex flex-column w-12"
}
const _hoisted_16 = { class: "text-900 mr-1" }
const _hoisted_17 = { class: "text-900" }

import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import Calendar from 'primevue/calendar';
import { RouterLink, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useOrganizationStore, useCalendarStore } from '@/store';
import { useRouting, useDateTime } from '@/composables';

interface ItemPropsInterface {
  description: string;
  accessTenant?: {
    full_name_user: string;
    id: number;
    tenant_id: number;
    tenant_name: string;
  };
  subject: {
    id: string;
    name: string;
    display_name?: string;
    person?: {
      id: string;
      display_name: string;
    };
    documentable: { id: number; full_name: string };
    therapy_event_type?: string;
    end_at: string;
    start_at: string;
  };
  properties: {
    tab: string;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationLogs',
  emits: ['date-select'],
  setup(__props, { emit: __emit }) {

const dates = ref();
const organizationStore = useOrganizationStore();
const $routing = useRouting();
const { organizationLogs } = storeToRefs(organizationStore);
const today = new Date();
const previousDatesApplied = ref<Date[]>();
const calendar = ref();
const dateWasChanged = ref(false);
const emit = __emit;
const calendarStore = useCalendarStore();
const route = useRouter();
const dateUtilities = useDateTime();

onBeforeMount(async () => {
  await defineFilters();
  await getLogs();
});

onMounted(() => {
  const month = today.getMonth();
  const year = today.getFullYear();
  const start = new Date(year, month, today.getDate() - 7);
  const end = new Date(year, month, today.getDate());
  const defaultDates = [start, end];
  previousDatesApplied.value = defaultDates;
  dates.value = defaultDates;
});

const getLogs = async () => {
  await organizationStore.getOrganizationLogs(Number($routing.route.params.id));
};

const onPageChange = (event) => {
  organizationLogs.value.filters.per_page = event.rows;
  $routing.addQueryParams({ per_page: organizationLogs.value.filters.per_page, page: event.page + 1 });
  defineFilters();
};

const defineFilters = async () => {
  const query = $routing.route.query;

  organizationLogs.value.filters.search = (query.search ? query.search : '') as string;
  organizationLogs.value.filters.page = query.page ? Number(query.page) : 1;
  organizationLogs.value.filters.per_page = query.per_page ? Number(query.per_page) : 15;
};

const onSearch = () => {
  if (organizationLogs.value.filters.search && organizationLogs.value.filters.search.length >= 3) {
    return $routing.addQueryParams({ search: organizationLogs.value.filters.search });
  }
};

const closeCalendar = () => {
  calendar.value.overlayVisible = false;
  if (dateWasChanged.value) {
    previousDatesApplied.value = dates.value;
    dateWasChanged.value = false;
  }
  dates.value = previousDatesApplied.value;
};

const applyDates = () => {
  organizationLogs.value.filters.start_at = useDateTime().formatDateForApi(dates.value[0]);
  organizationLogs.value.filters.end_at = useDateTime().formatDateForApi(dates.value[1]);
  emit('date-select', dates.value);
  dateWasChanged.value = true;
  closeCalendar();
};

const showChanges = (new_values, old_values) => !Array.isArray(new_values) && !Array.isArray(old_values);

const itemSubjectInformation = (item: ItemPropsInterface) => {
  if (item.description == 'logged in.') {
    return '';
  }

  if (item.subject.person) {
    return item.subject.person.display_name;
  }

  if (item.subject.therapy_event_type) {
    return `${item.subject.therapy_event_type} ${dateUtilities.formatHourMinute(
      item.subject.start_at,
      true,
    )} - ${dateUtilities.formatHourMinute(item.subject.end_at, true)}`;
  }

  if (item.subject.documentable) return item.subject.documentable.full_name;
  else return item.subject.name ?? item.subject.display_name;
};

const redirectToEventCalendar = async (log: ItemPropsInterface['subject']) => {
  if (log.therapy_event_type) {
    calendarStore.redirectedToDate = useDateTime().fromUTCStringToLocalDate(log.start_at as string);
    return route.replace(`/calendar/day`);
  }
};

const redirectTo = (item: ItemPropsInterface) => {
  if (item.properties.tab) {
    if (item.properties.tab === 'Profile' && item.subject.name) return `/organizations/${item.subject.id}/profile`;

    if (item.properties.tab === 'Profile' && (item.subject.person?.display_name || item.subject.display_name))
      return `/user/${item.subject.id}`;

    if (item.properties.tab === 'Connections' && item.subject.name) return `/organizations/${item.subject.id}`;

    if (item.properties.tab === 'Connections' && item.subject.person?.display_name)
      return `/user/${item.subject.id}/connections`;

    if (item.properties.tab === 'Documents') return `/user/${item.subject.id}/documents`;

    if (item.properties.tab === 'Treatment Plan') return `/user/${item.subject.id}/treatment-plans`;
  }
  return '';
};

const isActivityLog = computed<boolean>(() => $routing.route.name === 'organization.logs');

watch(
  () => $routing.route.query,
  async () => {
    if (isActivityLog.value) {
      await defineFilters();

      await getLogs();
    }
  },
);

watch(
  () => organizationLogs.value.filters.start_at && organizationLogs.value.filters.end_at,
  async () => {
    if (isActivityLog.value) {
      await getLogs();
    }
  },
);

watch(
  () => organizationLogs.value.filters.search,
  async () => {
    if (organizationLogs.value.filters.search === '') {
      $routing.removeQueryParam('search');
    }
  },
);

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_IconField, { "icon-position": "left" }, {
            default: _withCtx(() => [
              _createVNode(_component_InputIcon, null, {
                default: _withCtx(() => [
                  _createVNode(_component_Icon, {
                    name: "search",
                    class: "text-lg"
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_InputText, {
                modelValue: _unref(organizationStore).organizationLogs.filters.search,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(organizationStore).organizationLogs.filters.search) = $event)),
                type: "text",
                placeholder: "Search logs",
                onKeydown: _withKeys(_withModifiers(onSearch, ["prevent"]), ["enter"])
              }, null, 8, ["modelValue", "onKeydown"])
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_IconField, { "icon-position": "left" }, {
            default: _withCtx(() => [
              _createVNode(_component_InputIcon, { class: "z-1" }, {
                default: _withCtx(() => [
                  _createVNode(_component_Icon, {
                    name: "calendar_today",
                    class: "text-lg"
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(Calendar), {
                ref_key: "calendar",
                ref: calendar,
                modelValue: dates.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dates).value = $event)),
                "selection-mode": "range",
                "max-date": _unref(today),
                "hide-on-date-time-select": true,
                class: "daterangepicker-container w-17rem"
              }, {
                footer: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_AppButton, {
                      type: "secondary",
                      text: "Cancel",
                      class: "mr-2",
                      onClick: closeCalendar
                    }),
                    _createVNode(_component_AppButton, {
                      type: "primary",
                      text: "Apply",
                      disabled: !dates.value[1],
                      onClick: applyDates
                    }, null, 8, ["disabled"])
                  ])
                ]),
                _: 1
              }, 8, ["modelValue", "max-date"])
            ]),
            _: 1
          })
        ])
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_component_DataTable, {
        filters: _unref(organizationLogs).filters,
        "onUpdate:filters": _cache[2] || (_cache[2] = ($event: any) => ((_unref(organizationLogs).filters) = $event)),
        value: _unref(organizationLogs).data,
        paginator: true,
        rows: _unref(organizationLogs).filters.per_page,
        "total-records": _unref(organizationLogs).total,
        "data-key": "id",
        "row-hover": true,
        lazy: true,
        "loading-icon": "logo-spinner",
        loading: _unref(organizationLogs).loading,
        "rows-per-page-options": [15, 25, 30],
        "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
        "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
        "responsive-layout": "scroll",
        onPage: onPageChange
      }, {
        empty: _withCtx(() => [...(_cache[5] || (_cache[5] = [
          _createTextVNode("No Logs to View", -1)
        ]))]),
        loading: _withCtx(() => [...(_cache[6] || (_cache[6] = [
          _createTextVNode("Loading...", -1)
        ]))]),
        default: _withCtx(() => [
          _createVNode(_component_Column, {
            field: "created_at",
            header: "Datetime"
          }),
          _createVNode(_component_Column, {
            field: "description",
            header: "Event"
          }, {
            body: _withCtx(({ data }) => [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  (data.access_tenant)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(data.access_tenant.full_name_user), 1))
                    : _createCommentVNode("", true),
                  (data.impersonated_by)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        (!data.access_tenant)
                          ? (_openBlock(), _createBlock(_unref(RouterLink), {
                              key: 0,
                              to: `/user/${data.impersonated_by.id}`,
                              class: "font-semibold",
                              textContent: _toDisplayString(data.impersonated_by.display_name)
                            }, null, 8, ["to", "textContent"]))
                          : _createCommentVNode("", true),
                        _cache[3] || (_cache[3] = _createTextVNode(" ( as ", -1)),
                        (data.impersonated_by)
                          ? (_openBlock(), _createBlock(_unref(RouterLink), {
                              key: 1,
                              to: `/user/${data.causer.id}`,
                              class: "font-semibold"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(data.causer.display_name), 1)
                              ]),
                              _: 2
                            }, 1032, ["to"]))
                          : _createCommentVNode("", true),
                        _cache[4] || (_cache[4] = _createTextVNode(" ) ", -1))
                      ]))
                    : _createCommentVNode("", true),
                  (!data.impersonated_by && !data.access_tenant)
                    ? (_openBlock(), _createBlock(_unref(RouterLink), {
                        key: 2,
                        to: `/user/${data.causer.id}`,
                        class: "font-bold",
                        textContent: _toDisplayString(data.causer.display_name)
                      }, null, 8, ["to", "textContent"]))
                    : _createCommentVNode("", true),
                  (data.properties.student)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                        _createTextVNode(_toDisplayString(data.description.split(data.properties.student.name).shift()) + " ", 1),
                        _createVNode(_unref(RouterLink), {
                          class: "font-bold",
                          to: `/user/${data.properties.student.id}`,
                          textContent: _toDisplayString(data.properties.student.name)
                        }, null, 8, ["to", "textContent"]),
                        _createTextVNode(" " + _toDisplayString(data.description.split(data.properties.student.name).pop()), 1)
                      ]))
                    : _createCommentVNode("", true),
                  (data.properties.person)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                        _createTextVNode(_toDisplayString(data.description.split(data.properties.person.name).shift()) + " ", 1),
                        _createVNode(_unref(RouterLink), {
                          class: "font-bold",
                          to: `/user/${data.properties.person.id}`,
                          textContent: _toDisplayString(data.properties.person.name)
                        }, null, 8, ["to", "textContent"]),
                        _createTextVNode(" " + _toDisplayString(data.description.split(data.properties.person.name).pop()), 1)
                      ]))
                    : _createCommentVNode("", true),
                  (!data.properties.person && !data.properties.student)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(data.description), 1))
                    : _createCommentVNode("", true),
                  (data.subject.name)
                    ? (_openBlock(), _createBlock(_unref(RouterLink), {
                        key: 6,
                        to: `/organizations/${data.subject.id}/profile`,
                        class: "font-semibold",
                        onClick: ($event: any) => (_unref(organizationStore).getOrganization(data.subject.id)),
                        textContent: _toDisplayString(itemSubjectInformation(data))
                      }, null, 8, ["to", "onClick", "textContent"]))
                    : _createCommentVNode("", true),
                  (data.subject.person)
                    ? (_openBlock(), _createBlock(_unref(RouterLink), {
                        key: 7,
                        to: `/user/${data.subject.person.id}`,
                        class: "font-semibold",
                        textContent: _toDisplayString(itemSubjectInformation(data))
                      }, null, 8, ["to", "textContent"]))
                    : _createCommentVNode("", true),
                  (data.subject.therapy_event_type)
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 8,
                        class: "font-semibold",
                        onClick: ($event: any) => (redirectToEventCalendar(data.subject)),
                        textContent: _toDisplayString(itemSubjectInformation(data))
                      }, null, 8, _hoisted_12))
                    : _createCommentVNode("", true),
                  (data.subject.display_name)
                    ? (_openBlock(), _createBlock(_unref(RouterLink), {
                        key: 9,
                        to: `/user/${data.subject.id}`,
                        class: "font-semibold",
                        textContent: _toDisplayString(itemSubjectInformation(data))
                      }, null, 8, ["to", "textContent"]))
                    : _createCommentVNode("", true),
                  (data.properties?.tab)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_13, ">"))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(RouterLink), {
                    to: redirectTo(data),
                    class: "font-semibold",
                    textContent: _toDisplayString(data.properties.tab)
                  }, null, 8, ["to", "textContent"]),
                  (data.properties?.path)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_14, ">"))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", null, _toDisplayString(data.properties.path), 1)
                ]),
                (showChanges(data.properties.old_values, data.properties.new_values))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.properties.old_values, (old, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: index,
                          class: "text-sm flex align-items-center text-400"
                        }, [
                          _createElementVNode("span", _hoisted_16, _toDisplayString(index.toString().replaceAll('_', ' ')) + ":", 1),
                          _createElementVNode("span", null, _toDisplayString(old), 1),
                          old
                            ? (_openBlock(), _createBlock(_component_Icon, {
                                key: 0,
                                class: "mx-1",
                                name: "arrow_right_alt"
                              }))
                            : _createCommentVNode("", true),
                          _createElementVNode("span", _hoisted_17, _toDisplayString(data.properties.new_values[index]), 1)
                        ]))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["filters", "value", "rows", "total-records", "loading"])
    ]),
    _: 1
  }))
}
}

})